import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as api from '../services/api';
import { ConfirmNoteModal } from '../components/ConfirmNoteModal';
import { FilterBar } from '../components/FilterBar';
import { Modal } from '../components/Modal';
import { StatusBadge } from '../components/StatusBadge';
import { useAuth } from '../context/AuthContext';
import type { SchoolClass } from '../types/class';
import type {
  RegistrationAnswer,
  RegistrationForm,
  RegistrationFormField,
  RegistrationSubmission,
  RegistrationSubmissionStatus
} from '../types/registration';

type RegistrationTab = 'forms' | 'applications';
type FormActiveFilter = '' | 'active' | 'closed';
type ReviewDecision = 'accept' | 'reject' | 'clear-interview';

const SUBMISSION_STATUS_OPTIONS: Array<RegistrationSubmissionStatus | ''> = [
  '',
  'submitted',
  'interview',
  'registered',
  'rejected'
];

interface StudentAccountDraft {
  firstName: string;
  middleName: string;
  lastName: string;
  username: string;
  email: string;
  password: string;
  phone: string;
  age: string;
  gender: 'male' | 'female';
  cnic: string;
}

interface ReviewTarget {
  submission: RegistrationSubmission;
  decision: ReviewDecision;
}

interface PublicLinkModal {
  title: string;
  url: string;
}

const emptyStudentDraft = (): StudentAccountDraft => ({
  firstName: '',
  middleName: '',
  lastName: '',
  username: '',
  email: '',
  password: '',
  phone: '',
  age: '',
  gender: 'male',
  cnic: ''
});

function publicApplyUrl(formId: string): string {
  return `${window.location.origin}/apply/${formId}`;
}

function answerLookup(
  answers: RegistrationAnswer[],
  fields: RegistrationFormField[],
  patterns: RegExp[]
): string {
  for (const field of fields) {
    const haystack = `${field.key} ${field.label}`.toLowerCase();
    if (!patterns.some((pattern) => pattern.test(haystack))) continue;
    const answer = answers.find((item) => item.key === field.key);
    if (answer === undefined || answer.value === null || answer.value === '') continue;
    return String(answer.value).trim();
  }
  return '';
}

function generateTempPassword(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
  let value = '';
  for (let i = 0; i < 10; i += 1) {
    value += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return value;
}

function buildStudentDraftFromSubmission(
  submission: RegistrationSubmission,
  form: RegistrationForm | undefined
): StudentAccountDraft {
  const fields = form?.fields ?? [];
  const draft = emptyStudentDraft();

  draft.firstName = answerLookup(submission.answers, fields, [
    /^first.?name$/,
    /firstname/,
    /\bfirst\b/
  ]);
  draft.middleName = answerLookup(submission.answers, fields, [
    /^middle.?name$/,
    /middlename/,
    /\bmiddle\b/
  ]);
  draft.lastName = answerLookup(submission.answers, fields, [
    /^last.?name$/,
    /lastname/,
    /\blast\b/,
    /surname/
  ]);

  const fullName =
    answerLookup(submission.answers, fields, [
      /^full.?name$/,
      /\bname\b/,
      /applicant/,
      /student.?name/,
      /child.?name/
    ]) || submission.applicantLabel;

  if ((!draft.firstName || !draft.lastName) && fullName && fullName !== 'Applicant') {
    const parts = fullName.split(/\s+/).filter(Boolean);
    if (!draft.firstName && parts[0]) draft.firstName = parts[0];
    if (!draft.lastName && parts.length > 1) draft.lastName = parts.slice(1).join(' ');
    else if (!draft.lastName) draft.lastName = 'Student';
  }
  if (!draft.firstName) draft.firstName = 'Student';
  if (!draft.lastName) draft.lastName = 'Applicant';

  draft.email = answerLookup(submission.answers, fields, [/email/, /e-mail/]);
  draft.phone = answerLookup(submission.answers, fields, [/phone/, /mobile/, /contact/]);
  draft.age = answerLookup(submission.answers, fields, [/^age$/, /\bage\b/]);
  draft.cnic = answerLookup(submission.answers, fields, [/cnic/, /b.?form/, /national.?id/]).replace(
    /\D/g,
    ''
  );

  const genderRaw = answerLookup(submission.answers, fields, [/gender/, /sex/]).toLowerCase();
  if (genderRaw.startsWith('f')) draft.gender = 'female';
  else if (genderRaw.startsWith('m')) draft.gender = 'male';

  const emailLocal = draft.email.includes('@') ? draft.email.split('@')[0] : '';
  const usernameSeed = (emailLocal || `${draft.firstName}.${draft.lastName}`)
    .toLowerCase()
    .replace(/[^a-z0-9._]/g, '')
    .slice(0, 24);
  draft.username = usernameSeed || `student${Date.now().toString().slice(-6)}`;
  draft.password = generateTempPassword();

  return draft;
}

function decisionLabel(decision: ReviewDecision): string {
  if (decision === 'accept') return 'accept';
  if (decision === 'reject') return 'reject';
  return 'clear interview';
}

export function RegistrationPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const schoolId = user?.schoolId || null;
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<RegistrationTab>(
    searchParams.get('tab') === 'applications' ? 'applications' : 'forms'
  );

  const [forms, setForms] = useState<RegistrationForm[]>([]);
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busyFormId, setBusyFormId] = useState<string | null>(null);

  const [formSearchInput, setFormSearchInput] = useState('');
  const [formSearch, setFormSearch] = useState('');
  const [formActiveFilter, setFormActiveFilter] = useState<FormActiveFilter>('');

  const [submissions, setSubmissions] = useState<RegistrationSubmission[]>([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<RegistrationSubmissionStatus | ''>('submitted');
  const [formFilter, setFormFilter] = useState('');
  const [applicantSearchInput, setApplicantSearchInput] = useState('');
  const [applicantSearch, setApplicantSearch] = useState('');

  const [viewingSubmission, setViewingSubmission] = useState<RegistrationSubmission | null>(null);
  const [placementSubmission, setPlacementSubmission] = useState<RegistrationSubmission | null>(
    null
  );
  const [studentDraft, setStudentDraft] = useState<StudentAccountDraft>(emptyStudentDraft);
  const [busySubmissionId, setBusySubmissionId] = useState<string | null>(null);
  const [placementClassId, setPlacementClassId] = useState('');
  const [placing, setPlacing] = useState(false);
  const [reviewTarget, setReviewTarget] = useState<ReviewTarget | null>(null);
  const [publicLinkModal, setPublicLinkModal] = useState<PublicLinkModal | null>(null);
  const [copyingLink, setCopyingLink] = useState(false);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === 'forms') next.delete('tab');
        else next.set('tab', tab);
        return next;
      },
      { replace: true }
    );
  }, [tab, setSearchParams]);

  const loadForms = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    setError(null);
    try {
      const items = await api.listRegistrationForms(schoolId);
      setForms(items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load registration forms');
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  const loadClasses = useCallback(async () => {
    if (!schoolId) return;
    try {
      const items = await api.listClasses(schoolId, { activeOnly: true });
      setClasses(items);
    } catch {
      setClasses([]);
    }
  }, [schoolId]);

  const loadSubmissions = useCallback(async () => {
    if (!schoolId) return;
    setSubmissionsLoading(true);
    setError(null);
    try {
      const items = await api.listRegistrationSubmissions(schoolId, {
        status: statusFilter,
        formId: formFilter || undefined
      });
      setSubmissions(items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load applications');
    } finally {
      setSubmissionsLoading(false);
    }
  }, [schoolId, statusFilter, formFilter]);

  useEffect(() => {
    if (tab === 'forms') {
      void loadForms();
      void loadClasses();
    }
  }, [tab, loadForms, loadClasses]);

  useEffect(() => {
    if (tab === 'applications') {
      void loadForms();
      void loadClasses();
      void loadSubmissions();
    }
  }, [tab, loadForms, loadClasses, loadSubmissions]);

  const filteredForms = useMemo(() => {
    const query = formSearch.trim().toLowerCase();
    return forms.filter((form) => {
      if (formActiveFilter === 'active' && !form.isActive) return false;
      if (formActiveFilter === 'closed' && form.isActive) return false;
      if (!query) return true;
      const haystack = `${form.title} ${form.description ?? ''}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [forms, formSearch, formActiveFilter]);

  const filteredSubmissions = useMemo(() => {
    const query = applicantSearch.trim().toLowerCase();
    if (!query) return submissions;
    return submissions.filter((submission) =>
      submission.applicantLabel.toLowerCase().includes(query)
    );
  }, [submissions, applicantSearch]);

  function openPlacementModal(submission: RegistrationSubmission) {
    const form = forms.find((item) => item.id === submission.formId);
    setStudentDraft(buildStudentDraftFromSubmission(submission, form));
    setPlacementClassId(submission.assignedClassId || form?.autoEnrollClassId || '');
    setPlacementSubmission(submission);
    setViewingSubmission(null);
  }

  function closePlacementModal() {
    if (placing) return;
    setPlacementSubmission(null);
    setStudentDraft(emptyStudentDraft());
    setPlacementClassId('');
  }

  function updateStudentDraft<K extends keyof StudentAccountDraft>(
    key: K,
    value: StudentAccountDraft[K]
  ) {
    setStudentDraft((current) => ({ ...current, [key]: value }));
  }

  async function handleDelete(form: RegistrationForm) {
    if (!schoolId) return;
    const confirmed = window.confirm(
      `Delete “${form.title}”? It will be hidden from your list but kept in the system.`
    );
    if (!confirmed) return;

    setBusyFormId(form.id);
    setError(null);
    setSuccess(null);
    try {
      await api.deleteRegistrationForm(schoolId, form.id);
      setForms((current) => current.filter((item) => item.id !== form.id));
      setSuccess(`Deleted “${form.title}”.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete form');
    } finally {
      setBusyFormId(null);
    }
  }

  async function handleClose(form: RegistrationForm) {
    if (!schoolId) return;
    setBusyFormId(form.id);
    setError(null);
    setSuccess(null);
    try {
      const updated = await api.closeRegistrationForm(schoolId, form.id);
      setForms((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      setSuccess(`Closed “${updated.title}”.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to close form');
    } finally {
      setBusyFormId(null);
    }
  }

  async function handleCopyPublicLink(form: RegistrationForm) {
    const url = publicApplyUrl(form.id);
    setError(null);
    setSuccess(null);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setSuccess(`Copied public link for “${form.title}”.`);
        return;
      }
      setPublicLinkModal({ title: form.title, url });
    } catch {
      setPublicLinkModal({ title: form.title, url });
    }
  }

  async function copyPublicLinkFromModal() {
    if (!publicLinkModal) return;
    setCopyingLink(true);
    setError(null);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(publicLinkModal.url);
      } else {
        const input = document.createElement('input');
        input.value = publicLinkModal.url;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }
      setSuccess(`Copied public link for “${publicLinkModal.title}”.`);
      setPublicLinkModal(null);
    } catch {
      setError('Could not copy link. Select and copy it manually.');
    } finally {
      setCopyingLink(false);
    }
  }

  function requestReview(submission: RegistrationSubmission, decision: ReviewDecision) {
    setReviewTarget({ submission, decision });
  }

  async function confirmReview(note: string) {
    if (!schoolId || !reviewTarget) return;
    const { submission, decision } = reviewTarget;
    const label = decisionLabel(decision);
    setBusySubmissionId(submission.id);
    setError(null);
    setSuccess(null);
    try {
      let updated: RegistrationSubmission | null = null;
      if (decision === 'accept') {
        updated = await api.acceptRegistrationSubmission(
          schoolId,
          submission.id,
          note || undefined
        );
        setSuccess(`Accepted application from ${submission.applicantLabel}.`);
      } else if (decision === 'clear-interview') {
        updated = await api.clearRegistrationInterview(
          schoolId,
          submission.id,
          note || undefined
        );
        setSuccess(`Cleared interview for ${submission.applicantLabel}.`);
      } else {
        updated = await api.rejectRegistrationSubmission(
          schoolId,
          submission.id,
          note || undefined
        );
        setSuccess(`Rejected application from ${submission.applicantLabel}.`);
      }
      setReviewTarget(null);
      setViewingSubmission(null);
      await loadSubmissions();
      if (
        updated &&
        updated.status === 'registered' &&
        !updated.studentUserId &&
        (decision === 'clear-interview' || decision === 'accept')
      ) {
        openPlacementModal(updated);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${label} application`);
    } finally {
      setBusySubmissionId(null);
    }
  }

  async function handlePlaceStudent(event: FormEvent) {
    event.preventDefault();
    if (!schoolId || !placementSubmission) return;

    const form = forms.find((item) => item.id === placementSubmission.formId);
    const classId = form?.autoEnrollStudent
      ? placementSubmission.assignedClassId || form.autoEnrollClassId || undefined
      : placementClassId || undefined;

    if (!classId) {
      setError('Select a class for this student');
      return;
    }

    const age = Number(studentDraft.age);
    if (!Number.isFinite(age)) {
      setError('Age is required');
      return;
    }
    if (!/^\d{13}$/.test(studentDraft.cnic.replace(/\D/g, ''))) {
      setError('CNIC must be 13 digits');
      return;
    }

    setPlacing(true);
    setError(null);
    setSuccess(null);
    try {
      const { user: createdUser } = await api.registerStudent({
        firstName: studentDraft.firstName.trim(),
        middleName: studentDraft.middleName.trim() || undefined,
        lastName: studentDraft.lastName.trim(),
        username: studentDraft.username.trim(),
        email: studentDraft.email.trim(),
        password: studentDraft.password,
        phone: studentDraft.phone.trim() || undefined,
        age,
        gender: studentDraft.gender,
        cnic: studentDraft.cnic.replace(/\D/g, ''),
        schoolId
      });

      const assigned = await api.assignRegistrationClass(schoolId, placementSubmission.id, {
        classId: form?.autoEnrollStudent ? undefined : classId,
        studentUserId: createdUser.id
      });

      setSubmissions((current) =>
        current.map((item) => (item.id === assigned.id ? assigned : item))
      );
      setSuccess(
        `Created student ${createdUser.fullName} and assigned to ${classNameFor(assigned.assignedClassId)}.`
      );
      setPlacementSubmission(null);
      setStudentDraft(emptyStudentDraft());
      setPlacementClassId('');
      await loadSubmissions();
      await loadClasses();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create student and assign class');
    } finally {
      setPlacing(false);
    }
  }

  function formTitleFor(formId: string): string {
    return forms.find((form) => form.id === formId)?.title || formId;
  }

  function classNameFor(classId: string | null | undefined): string {
    if (!classId) return '—';
    return classes.find((item) => item.id === classId)?.name || classId;
  }

  function fieldLabelFor(formId: string, key: string): string {
    const field = forms.find((form) => form.id === formId)?.fields.find((item) => item.key === key);
    return field?.label || key;
  }

  function formatAnswerValue(value: string | number | boolean): string {
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  }

  function submissionActions(submission: RegistrationSubmission) {
    if (submission.status === 'submitted') {
      return (
        <>
          <button
            type="button"
            className="ghost-btn compact"
            disabled={busySubmissionId === submission.id}
            onClick={() => requestReview(submission, 'accept')}
          >
            Accept
          </button>
          <button
            type="button"
            className="ghost-btn compact"
            disabled={busySubmissionId === submission.id}
            onClick={() => requestReview(submission, 'reject')}
          >
            Reject
          </button>
        </>
      );
    }
    if (submission.status === 'interview') {
      return (
        <>
          <button
            type="button"
            className="ghost-btn compact"
            disabled={busySubmissionId === submission.id}
            onClick={() => requestReview(submission, 'clear-interview')}
          >
            Clear interview
          </button>
          <button
            type="button"
            className="ghost-btn compact"
            disabled={busySubmissionId === submission.id}
            onClick={() => requestReview(submission, 'reject')}
          >
            Reject
          </button>
        </>
      );
    }
    return null;
  }

  if (!schoolId) {
    return (
      <section className="admin-panel">
        <div className="panel-heading">
          <div>
            <h2>Registration</h2>
            <p>Your account is not linked to a school yet.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="admin-panel schools-page">
      <div className="panel-heading">
        <div>
          <h2>Registration</h2>
          <p>
            Manage school registration forms and advance applications through review, interview, and
            registration.
          </p>
        </div>
        {tab === 'forms' ? (
          <span className="panel-meta">{filteredForms.length} forms</span>
        ) : null}
        {tab === 'applications' ? (
          <span className="panel-meta">{filteredSubmissions.length} applications</span>
        ) : null}
      </div>

      <div className="tab-bar" role="tablist" aria-label="Registration sections">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'forms'}
          className={tab === 'forms' ? 'tab-btn active' : 'tab-btn'}
          onClick={() => {
            setTab('forms');
            setError(null);
            setSuccess(null);
          }}
        >
          Create / manage forms
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'applications'}
          className={tab === 'applications' ? 'tab-btn active' : 'tab-btn'}
          onClick={() => {
            setTab('applications');
            setError(null);
            setSuccess(null);
          }}
        >
          View applications
        </button>
      </div>

      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
      {success ? (
        <p className="form-success" role="status">
          {success}
        </p>
      ) : null}

      {tab === 'forms' ? (
        <>
          <FilterBar
            className="classes-toolbar"
            onSubmit={(event) => {
              event.preventDefault();
              setFormSearch(formSearchInput.trim());
            }}
          >
            <label>
              Search
              <input
                type="search"
                value={formSearchInput}
                onChange={(e) => setFormSearchInput(e.target.value)}
                placeholder="Title or description"
              />
            </label>
            <label>
              Status
              <select
                value={formActiveFilter}
                onChange={(e) => setFormActiveFilter(e.target.value as FormActiveFilter)}
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="closed">Closed</option>
              </select>
            </label>
            <button type="submit" className="primary-btn">
              Apply
            </button>
            <button type="button" className="ghost-btn" onClick={() => void loadForms()}>
              Refresh
            </button>
            <button
              type="button"
              className="ghost-btn"
              onClick={() => navigate('/registration/forms/new')}
            >
              New form
            </button>
          </FilterBar>

          {loading ? <p className="muted">Loading forms…</p> : null}
          {!loading && filteredForms.length === 0 ? (
            <p className="muted">
              {forms.length === 0 ? 'No registration forms yet.' : 'No forms match these filters.'}
            </p>
          ) : null}

          {filteredForms.length > 0 ? (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Auto enroll</th>
                    <th>Fields</th>
                    <th>Active</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredForms.map((form) => (
                    <tr key={form.id}>
                      <td>
                        <strong>{form.title}</strong>
                        {form.description ? (
                          <div className="cell-sub">{form.description}</div>
                        ) : null}
                      </td>
                      <td>
                        {form.autoEnrollStudent ? classNameFor(form.autoEnrollClassId) : 'No'}
                      </td>
                      <td>{form.fields.length}</td>
                      <td>
                        <StatusBadge status={form.isActive ? 'active' : 'closed'} />
                      </td>
                      <td>
                        <div className="action-row">
                          <button
                            type="button"
                            className="ghost-btn compact"
                            onClick={() => navigate(`/registration/forms/${form.id}/edit`)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="ghost-btn compact"
                            onClick={() =>
                              navigate(`/registration/forms/new?copyFrom=${form.id}`)
                            }
                          >
                            Copy form
                          </button>
                          {form.isActive ? (
                            <button
                              type="button"
                              className="ghost-btn compact"
                              onClick={() => void handleCopyPublicLink(form)}
                            >
                              Copy public link
                            </button>
                          ) : null}
                          {form.isActive ? (
                            <button
                              type="button"
                              className="ghost-btn compact"
                              disabled={busyFormId === form.id}
                              onClick={() => void handleClose(form)}
                            >
                              Close
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="ghost-btn compact"
                            disabled={busyFormId === form.id}
                            onClick={() => void handleDelete(form)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      ) : null}

      {tab === 'applications' ? (
        <section className="settings-section">
          <div className="settings-section-header">
            <h3>View applications</h3>
            <p>
              Advance applications through submission review, interview, and registration. Assign a
              class when an applicant is registered.
            </p>
          </div>
          <div className="settings-section-body">
            <FilterBar
              onSubmit={(event) => {
                event.preventDefault();
                setApplicantSearch(applicantSearchInput.trim());
              }}
            >
              <label>
                Applicant
                <input
                  type="search"
                  value={applicantSearchInput}
                  onChange={(e) => setApplicantSearchInput(e.target.value)}
                  placeholder="Search by name"
                />
              </label>
              <label>
                Status
                <select
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(e.target.value as RegistrationSubmissionStatus | '')
                  }
                >
                  {SUBMISSION_STATUS_OPTIONS.map((option) => (
                    <option key={option || 'all'} value={option}>
                      {option || 'All'}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Form
                <select value={formFilter} onChange={(e) => setFormFilter(e.target.value)}>
                  <option value="">All forms</option>
                  {forms.map((form) => (
                    <option key={form.id} value={form.id}>
                      {form.title}
                    </option>
                  ))}
                </select>
              </label>
              <button type="submit" className="primary-btn">
                Apply
              </button>
            </FilterBar>

            {submissionsLoading ? <p className="muted">Loading applications…</p> : null}
            {!submissionsLoading && filteredSubmissions.length === 0 ? (
              <p className="muted">
                {submissions.length === 0
                  ? 'No applications to show.'
                  : 'No applications match this applicant search.'}
              </p>
            ) : null}

            {filteredSubmissions.length > 0 ? (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Applicant</th>
                      <th>Form</th>
                      <th>Submitted</th>
                      <th>Status</th>
                      <th>Class</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSubmissions.map((submission) => (
                      <tr key={submission.id}>
                        <td>
                          <strong>{submission.applicantLabel}</strong>
                          {submission.reviewNote ? (
                            <div className="cell-sub">Note: {submission.reviewNote}</div>
                          ) : null}
                        </td>
                        <td>{formTitleFor(submission.formId)}</td>
                        <td>
                          {submission.createdAt
                            ? new Date(submission.createdAt).toLocaleString()
                            : '—'}
                        </td>
                        <td>
                          <StatusBadge status={submission.status} />
                        </td>
                        <td>{classNameFor(submission.assignedClassId)}</td>
                        <td>
                          <div className="action-row">
                            <button
                              type="button"
                              className="ghost-btn compact"
                              onClick={() => setViewingSubmission(submission)}
                            >
                              View answers
                            </button>
                            {submission.status === 'registered' && !submission.studentUserId ? (
                              <button
                                type="button"
                                className="ghost-btn compact"
                                onClick={() => openPlacementModal(submission)}
                              >
                                Place in class
                              </button>
                            ) : null}
                            {submissionActions(submission)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      <Modal
        open={Boolean(viewingSubmission)}
        title="Submitted application"
        subtitle={
          viewingSubmission
            ? `${viewingSubmission.applicantLabel} · ${formTitleFor(viewingSubmission.formId)}`
            : undefined
        }
        size="lg"
        onClose={() => setViewingSubmission(null)}
        actions={
          viewingSubmission ? (
            <>
              {viewingSubmission.status === 'submitted' ? (
                <>
                  <button
                    type="button"
                    className="ghost-btn"
                    disabled={busySubmissionId === viewingSubmission.id}
                    onClick={() => requestReview(viewingSubmission, 'reject')}
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    className="primary-btn"
                    disabled={busySubmissionId === viewingSubmission.id}
                    onClick={() => requestReview(viewingSubmission, 'accept')}
                  >
                    Accept
                  </button>
                </>
              ) : null}
              {viewingSubmission.status === 'interview' ? (
                <>
                  <button
                    type="button"
                    className="ghost-btn"
                    disabled={busySubmissionId === viewingSubmission.id}
                    onClick={() => requestReview(viewingSubmission, 'reject')}
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    className="primary-btn"
                    disabled={busySubmissionId === viewingSubmission.id}
                    onClick={() => requestReview(viewingSubmission, 'clear-interview')}
                  >
                    Clear interview
                  </button>
                </>
              ) : null}
              {viewingSubmission.status === 'registered' && !viewingSubmission.studentUserId ? (
                <button
                  type="button"
                  className="primary-btn"
                  onClick={() => openPlacementModal(viewingSubmission)}
                >
                  Place in class
                </button>
              ) : null}
            </>
          ) : null
        }
      >
        {viewingSubmission ? (
          <>
            <div className="modal-meta">
              <div>
                <span className="modal-meta-label">Status</span>
                <StatusBadge status={viewingSubmission.status} />
              </div>
              <div>
                <span className="modal-meta-label">Submitted</span>
                <span>
                  {viewingSubmission.createdAt
                    ? new Date(viewingSubmission.createdAt).toLocaleString()
                    : '—'}
                </span>
              </div>
              <div>
                <span className="modal-meta-label">Assigned class</span>
                <span>{classNameFor(viewingSubmission.assignedClassId)}</span>
              </div>
            </div>

            <h4>Answers</h4>
            {viewingSubmission.answers.length === 0 ? (
              <p className="muted">No answers were submitted.</p>
            ) : (
              <dl className="submission-answer-list">
                {viewingSubmission.answers.map((answer) => (
                  <div key={answer.key} className="submission-answer-row">
                    <dt>{fieldLabelFor(viewingSubmission.formId, answer.key)}</dt>
                    <dd>{formatAnswerValue(answer.value)}</dd>
                  </div>
                ))}
              </dl>
            )}

            {viewingSubmission.reviewNote ? (
              <div className="submission-review-note">
                <h4>Review note</h4>
                <p>{viewingSubmission.reviewNote}</p>
              </div>
            ) : null}
          </>
        ) : null}
      </Modal>

      <Modal
        open={Boolean(placementSubmission)}
        title="Place student in class"
        subtitle="Create a student account from this application, then assign a class."
        size="lg"
        className="placement-modal"
        asForm
        onSubmit={handlePlaceStudent}
        onClose={closePlacementModal}
        closeDisabled={placing}
        closeOnBackdrop={!placing}
        actions={
          <>
            <button
              type="button"
              className="ghost-btn"
              disabled={placing}
              onClick={() => closePlacementModal()}
            >
              Later
            </button>
            <button type="submit" className="primary-btn" disabled={placing}>
              {placing ? 'Creating…' : 'Create student & assign class'}
            </button>
          </>
        }
      >
        {placementSubmission ? (
          <>
            <div className="placement-section">
              <h4>Student account</h4>
              <p className="muted">
                Prefills from registration answers where possible. Complete any missing required
                fields before saving.
              </p>
              <div className="form-grid">
                <label>
                  First name
                  <input
                    required
                    value={studentDraft.firstName}
                    onChange={(e) => updateStudentDraft('firstName', e.target.value)}
                  />
                </label>
                <label>
                  Middle name
                  <input
                    value={studentDraft.middleName}
                    onChange={(e) => updateStudentDraft('middleName', e.target.value)}
                    placeholder="Optional"
                  />
                </label>
                <label>
                  Last name
                  <input
                    required
                    value={studentDraft.lastName}
                    onChange={(e) => updateStudentDraft('lastName', e.target.value)}
                  />
                </label>
                <label>
                  Username
                  <input
                    required
                    value={studentDraft.username}
                    onChange={(e) => updateStudentDraft('username', e.target.value)}
                  />
                </label>
                <label>
                  Email
                  <input
                    required
                    type="email"
                    value={studentDraft.email}
                    onChange={(e) => updateStudentDraft('email', e.target.value)}
                  />
                </label>
                <label>
                  Temporary password
                  <input
                    required
                    value={studentDraft.password}
                    onChange={(e) => updateStudentDraft('password', e.target.value)}
                  />
                </label>
                <label>
                  Phone
                  <input
                    value={studentDraft.phone}
                    onChange={(e) => updateStudentDraft('phone', e.target.value)}
                    placeholder="Optional"
                  />
                </label>
                <label>
                  Age
                  <input
                    required
                    type="number"
                    min={3}
                    max={25}
                    value={studentDraft.age}
                    onChange={(e) => updateStudentDraft('age', e.target.value)}
                  />
                </label>
                <label>
                  Gender
                  <select
                    value={studentDraft.gender}
                    onChange={(e) =>
                      updateStudentDraft('gender', e.target.value as 'male' | 'female')
                    }
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </label>
                <label>
                  CNIC
                  <input
                    required
                    value={studentDraft.cnic}
                    onChange={(e) => updateStudentDraft('cnic', e.target.value)}
                    placeholder="13 digits"
                  />
                </label>
              </div>
            </div>

            <div className="placement-section">
              <h4>Class assignment</h4>
              {forms.find((item) => item.id === placementSubmission.formId)?.autoEnrollStudent ? (
                <p className="muted">
                  Auto-enroll class:{' '}
                  <strong>
                    {classNameFor(
                      placementSubmission.assignedClassId ||
                        forms.find((item) => item.id === placementSubmission.formId)
                          ?.autoEnrollClassId
                    )}
                  </strong>
                </p>
              ) : (
                <label>
                  Class
                  <select
                    required
                    value={placementClassId}
                    onChange={(e) => setPlacementClassId(e.target.value)}
                  >
                    <option value="">Select a class</option>
                    {classes.map((classDoc) => (
                      <option key={classDoc.id} value={classDoc.id}>
                        {classDoc.name}
                        {classDoc.grade ? ` (${classDoc.grade})` : ''}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          </>
        ) : null}
      </Modal>

      <Modal
        open={Boolean(publicLinkModal)}
        title="Public registration link"
        subtitle={
          publicLinkModal ? `Share this link for “${publicLinkModal.title}”.` : undefined
        }
        size="sm"
        onClose={() => setPublicLinkModal(null)}
        actions={
          <>
            <button
              type="button"
              className="ghost-btn"
              disabled={copyingLink}
              onClick={() => setPublicLinkModal(null)}
            >
              Close
            </button>
            <button
              type="button"
              className="primary-btn"
              disabled={copyingLink}
              onClick={() => void copyPublicLinkFromModal()}
            >
              {copyingLink ? 'Copying…' : 'Copy'}
            </button>
          </>
        }
      >
        {publicLinkModal ? (
          <label>
            URL
            <input readOnly value={publicLinkModal.url} onFocus={(e) => e.target.select()} />
          </label>
        ) : null}
      </Modal>

      <ConfirmNoteModal
        open={Boolean(reviewTarget)}
        title={
          reviewTarget
            ? `${decisionLabel(reviewTarget.decision).replace(/^\w/, (c) => c.toUpperCase())} application`
            : 'Confirm'
        }
        message={
          reviewTarget
            ? `Optional review note for ${decisionLabel(reviewTarget.decision)} of ${reviewTarget.submission.applicantLabel}.`
            : ''
        }
        confirmLabel={
          reviewTarget
            ? decisionLabel(reviewTarget.decision).replace(/^\w/, (c) => c.toUpperCase())
            : 'Confirm'
        }
        noteLabel="Optional review note"
        notePlaceholder="Add a note (optional)"
        busy={Boolean(reviewTarget && busySubmissionId === reviewTarget.submission.id)}
        onCancel={() => setReviewTarget(null)}
        onConfirm={confirmReview}
      />
    </section>
  );
}
